import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { openModal } from 'reducers/modalReducer';
import { checkCancelLimits } from 'data/reducers/subscriptionReducer';
import { MODAL_TYPES } from 'view/ModalManager';
import './ManageSubscription.css';
import _ from 'lodash';
import { SUBSCRIPTION_PLANS } from 'constants/subscriptions';
import { useOrganizations } from 'data/repositories/organizationsRepository';

const ManageSubscription = ({
	availablePlans = [],
	currentPlan = null,
	subscriptionStatus = 'active',
}) => {
	const dispatch = useDispatch();
	const { active: activeOrganization } = useOrganizations();
	const { isPerformingAction } = useSelector((state) => ({
		isPerformingAction: state.subscription?.isPerformingAction || false,
	}));

	// Check if current plan is enterprise
	const isEnterprise =
		currentPlan?.name?.toLowerCase().includes(SUBSCRIPTION_PLANS.ENTERPRISE) ||
		currentPlan?.slug?.toLowerCase().includes(SUBSCRIPTION_PLANS.ENTERPRISE);

	const openSubscriptionModal = (action, planId = null, planName = null) => {
		dispatch(
			openModal('SUBSCRIPTION_ACTION', {
				action,
				planId,
				planName,
			}),
		);
	};

	const handleUpgradePlan = () => {
		// For upgrade, you might want to show a plan selection first
		// For now, let's assume there's a premium plan available
		// const upgradePlan = availablePlans.find(
		// 	(plan) => plan.tier > (currentPlan?.tier || 0)
		// );

		const upgradePlan = _.last(availablePlans);

		if (upgradePlan) {
			openSubscriptionModal('upgrade', upgradePlan.id, upgradePlan.name);
		} else {
			// Fallback - open portal for plan selection
			openSubscriptionModal('portal');
		}
	};

	const handleDowngradePlan = () => {
		// Open downgrade modal - limit checking will be done when user selects a plan
		openSubscriptionModal('downgrade');
	};

	const handlePausePlan = () => {
		openSubscriptionModal('pause');
	};

	const handleResumePlan = () => {
		openSubscriptionModal('resume');
	};

	const handleCancelPlan = () => {
		dispatch(
			openModal(MODAL_TYPES.CONFIRM, {
				title: 'Are you sure you want to cancel the plan?',
				message:
					"This will downgrade you to the free plan. You'll lose access to key features like email alerts, billing history, and your active properties dashboard.",
				confirmLabel: 'Yes, Cancel Plan',
				closeLabel: 'Keep Subscription',
				color: 'danger',
				onConfirm: () => {
					// After confirmation, check limits and proceed with cancel flow
					dispatch(checkCancelLimits());
				},
			}),
		);
	};

	const handleManageBilling = () => {
		openSubscriptionModal('portal');
	};

	// Determine if subscription is paused/cancelled
	const isPaused =
		subscriptionStatus === 'paused' || subscriptionStatus === 'cancelled';
	const isActive =
		subscriptionStatus === 'active' || subscriptionStatus === 'trial';

	// A solo subscriber (no active organization) always acts on their own
	// subscription and keeps full control. Within an organization, only the
	// owner (organizations.owner_id) may change the subscription -- an
	// organization admin is deliberately excluded. membership_type is
	// computed server-side as owner_id === current user id, so it needs no
	// client-side comparison against the logged-in user.
	const isSubscriptionOwner =
		!activeOrganization || activeOrganization.membership_type === 'owned';

	return (
		<div className="manage-subscription">
			{activeOrganization && (
				<div className="alert alert-info mb-4">
					<div className="d-flex align-items-center">
						<i className="bi bi-building fs-4 me-3"></i>
						<div>
							<div className="fw-bold">Managing Subscription For:</div>
							<div className="text-muted">{activeOrganization.name}</div>
						</div>
					</div>
				</div>
			)}
			<div className="manage-subscription-buttons">
				{/* Non-owners can see the plan (rendered by the parent) but cannot
				    act on it -- explain who can, so they don't mistake this for a
				    broken feature. */}
				{!isSubscriptionOwner && !isEnterprise && (
					<p className="text-muted mb-0 manage-subscription-owner-notice">
						Only the organization owner can upgrade, downgrade, pause,
						resume, or cancel this subscription. Contact your organization
						owner to make changes.
					</p>
				)}

				{/* Enterprise plan message */}
				{isEnterprise && isActive && (
					<div className="alert alert-info">
						<strong>Enterprise Plan</strong>
						<p className="mb-0 mt-2">
							You're on a custom Enterprise plan. To modify your subscription or
							limits, please contact our support team.
						</p>
						<button
							className="btn btn-sm btn-primary mt-3"
							onClick={() => window.open('mailto:admin@violerts.com')}
						>
							Contact Support
						</button>
					</div>
				)}

				{/* Show Resume button if subscription is paused and not enterprise */}
				{isPaused && !isEnterprise && isSubscriptionOwner && (
					<button
						className="btn btn-success manage-btn manage-btn-resume"
						onClick={handleResumePlan}
						disabled={isPerformingAction}
					>
						{isPerformingAction ? 'Processing...' : 'Resume Plan'}
					</button>
				)}

				{/* Show active subscription management buttons if subscription is active and not enterprise */}
				{isActive && !isEnterprise && isSubscriptionOwner && (
					<>
						<button
							className="btn btn-primary manage-btn manage-btn-upgrade"
							onClick={handleUpgradePlan}
							disabled={isPerformingAction}
						>
							{isPerformingAction ? 'Processing...' : 'Upgrade Plan'}
						</button>

						<button
							className="btn manage-btn manage-btn-secondary"
							onClick={handleDowngradePlan}
							disabled={isPerformingAction}
						>
							{isPerformingAction ? 'Processing...' : 'Downgrade Plan'}
						</button>

						{false && (
							<button
								className="btn manage-btn manage-btn-secondary"
								onClick={handlePausePlan}
								disabled={isPerformingAction}
							>
								{isPerformingAction ? 'Processing...' : 'Pause Plan'}
							</button>
						)}

						<button
							className="manage-btn manage-btn-cancel"
							onClick={handleCancelPlan}
							disabled={isPerformingAction}
						>
							{isPerformingAction ? 'Processing...' : 'Cancel Plan'}
						</button>
					</>
				)}

				{isEnterprise && (
					<a
						href="mailto:admin@violerts.com"
						className="btn btn-primary manage-btn manage-btn-upgrade"
					>
						Reach out to Customer Support to Manage Your Plan
					</a>
				)}

				{/* Always show Manage Billing button */}
				{false && (
					<button
						className="btn btn-outline-primary manage-btn"
						onClick={handleManageBilling}
						disabled={isPerformingAction}
					>
						{isPerformingAction ? 'Processing...' : 'Manage Billing'}
					</button>
				)}
			</div>
		</div>
	);
};

export default ManageSubscription;
